import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import {
  ThemeConfig,
  ThemePreset,
  ThemeService,
  THEME_PRESETS,
} from 'src/app/private/services/theme.service';
import { ManageBusinessInfoService } from 'src/app/private/services/manage-business-info.service';
import { UploadService } from 'src/app/private/services/upload.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-white-label-panel',
  templateUrl: './white-label-panel.component.html',
  styleUrls: ['./white-label-panel.component.scss'],
})
export class WhiteLabelPanelComponent implements OnInit, OnDestroy {
  @Input() isOpen = false;
  @Output() closePanel = new EventEmitter<void>();

  config!: ThemeConfig;
  presets: ThemePreset[] = THEME_PRESETS;
  sidebarLogoPreview: string = '';
  navbarLogoPreview: string = '';
  syncButtonWithPrimary = false;
  isSidebarLogoUploading = false;
  isNavbarLogoUploading = false;
  isSaving = false;

  buttonColorPresets = [
    { label: 'Green',     color: '#4e9c2f' },
    { label: 'Navy',      color: '#0d4259' },
    { label: 'Dark Green',color: '#1a7a4a' },
    { label: 'Teal',      color: '#008080' },
    { label: 'Purple',    color: '#5c2d91' },
    { label: 'Red',       color: '#c0392b' },
    { label: 'Orange',    color: '#e67e22' },
    { label: 'Dark Blue', color: '#1a1a2e' },
    { label: 'Emerald',   color: '#065f46' },
    { label: 'Indigo',    color: '#3949ab' },
    { label: 'Crimson',   color: '#8b1a1a' },
    { label: 'Teal Dark', color: '#0f766e' },
    { label: 'Blue',      color: '#1e40af' },
    { label: 'Slate',     color: '#475569' },
    { label: 'Rose',      color: '#9f1239' },
    { label: 'Amber',     color: '#b45309' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private themeService: ThemeService,
    private businessInfoService: ManageBusinessInfoService,
    private uploadService: UploadService,
    private localStorageService: LocalStorageService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.themeService.config$
      .pipe(takeUntil(this.destroy$))
      .subscribe((cfg) => {
        this.config = { ...cfg };
        this.sidebarLogoPreview = cfg.sidebarLogoUrl;
        this.navbarLogoPreview = cfg.navbarLogoUrl;
      });
  }

  onPrimaryChange(color: string): void {
    this.themeService.updatePrimary(color);
  }

  onAccentChange(color: string): void {
    this.themeService.updateAccent(color);
  }

  onButtonColorChange(color: string): void {
    this.themeService.updateButtonColor(color);
  }

  syncButtonToPrimary(sync: boolean): void {
    this.syncButtonWithPrimary = sync;
    if (sync) {
      this.themeService.updateButtonColor(this.config.primaryColor);
    }
  }

  onDarkModeToggle(enabled: boolean): void {
    this.themeService.toggleDarkMode(enabled);
  }

  applyPreset(preset: ThemePreset): void {
    this.themeService.applyPreset(preset);
  }

  onSidebarLogoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.toastr.error('File size must be less than 2 MB');
      return;
    }
    this._uploadLogoToS3(file, (url) => {
      this.sidebarLogoPreview = url;
      this.themeService.updateSidebarLogo(url);
    }, 'sidebar');
  }

  removeSidebarLogo(): void {
    this.sidebarLogoPreview = '';
    this.themeService.updateSidebarLogo('');
  }

  onNavbarLogoFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';
    if (!file.type.startsWith('image/')) {
      this.toastr.error('Please upload an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      this.toastr.error('File size must be less than 2 MB');
      return;
    }
    this._uploadLogoToS3(file, (url) => {
      this.navbarLogoPreview = url;
      this.themeService.updateNavbarLogo(url);
    }, 'navbar');
  }

  removeNavbarLogo(): void {
    this.navbarLogoPreview = '';
    this.themeService.updateNavbarLogo('');
  }

  private _uploadLogoToS3(file: File, callback: (url: string) => void, type: 'sidebar' | 'navbar'): void {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folders', `public/client-${this.localStorageService.getClientId()}/white-label`);
    formData.append('bucket', 'emruploads');

    if (type === 'sidebar') this.isSidebarLogoUploading = true;
    else this.isNavbarLogoUploading = true;

    this.uploadService.uploadDocumentsWithProgress(formData).subscribe({
      next: (event: any) => {
        if (event.type === 'response') {
          if (type === 'sidebar') this.isSidebarLogoUploading = false;
          else this.isNavbarLogoUploading = false;
          const url = event.body?.url || event.body?.data?.url || '';
          if (url) {
            callback(url);
          } else {
            this.toastr.error('Upload failed: No URL returned');
          }
        }
      },
      error: () => {
        if (type === 'sidebar') this.isSidebarLogoUploading = false;
        else this.isNavbarLogoUploading = false;
        this.toastr.error(`Failed to upload ${type} logo`);
      },
    });
  }

  onAppNameChange(name: string): void {
    this.themeService.updateAppName(name);
  }

  resetAll(): void {
    this.themeService.resetToDefault();
  }

  saveSettings(): void {
    this.isSaving = true;
    const clientId = this.localStorageService.getClientId();

    this.businessInfoService.getClient({ client_id: clientId }).subscribe({
      next: (res: any) => {
        const clientData = res?.data || res;
        const existingOtherSettings = clientData?.other_settings || {};
        const updatedClient = {
          ...clientData,
          client_id: clientId,
          other_settings: {
            ...existingOtherSettings,
            white_label: this.themeService.currentConfig,
          },
        };

        this.businessInfoService.updateClient(updatedClient).subscribe({
          next: () => {
            // Also persist to localStorage so the next init() picks it up
            try {
              localStorage.setItem(
                'crm_white_label_theme',
                JSON.stringify(this.themeService.currentConfig)
              );
            } catch { /* ignore quota errors */ }
            this.isSaving = false;
            this.toastr.success('White label settings saved successfully');
            this.closePanel.emit();
          },
          error: (err: any) => {
            this.isSaving = false;
            this.toastr.error(err?.error?.msg || 'Failed to save white label settings');
          },
        });
      },
      error: () => {
        this.isSaving = false;
        this.toastr.error('Failed to fetch client data');
      },
    });
  }

  close(): void {
    this.closePanel.emit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
